import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Sparkles } from 'lucide-react'
import { CreateTenantModal } from '../components/CreateTenantModal'
import { CreateDemoTenantModal } from '../components/CreateDemoTenantModal'
import { DemoTenantBanner } from '../components/DemoTenantBanner'
import { useAuth } from '../contexts/AuthContext'
import { useTenantDirectory } from '../lib/tenantDirectory'
import { toTenantModel, tenantView, RANK, makeMatcher, initials, formatRelative } from '../lib/support'
import {
  Avatar,
  Dot,
  Tag,
  StatusLabel,
  PageHeader,
  SearchInput,
  FilterChip,
  ProgressBar,
  Spinner,
  ErrorBanner,
} from '../components/ui'

const PAGE_SIZE = 20

// Filter keys are persisted in ?filter= so Today's KPI cards can deep-link here.
const FILTERS = [
  { key: 'all', label: 'Todos', test: () => true },
  { key: 'attention', label: 'Precisam de atenção', test: (v) => v.needsAttention },
  { key: 'wa', label: 'Problemas no WhatsApp', test: (v) => v.wa.tone === 'crit' || v.wa.tone === 'warn' },
  { key: 'billing', label: 'Problemas de cobrança', test: (v) => ['past_due', 'awaiting_payment'].includes(v.model.billing) },
  { key: 'login', label: 'Não conseguem entrar', test: (v) => v.owner.label === 'Bloqueado' || v.owner.label === 'Sem proprietário' },
  { key: 'suspended', label: 'Suspensos', test: (v) => !v.model.active },
]

export const TenantsPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // Staff: view + activate/suspend only. Create, edit and purge are owner-only.
  const { isOwner } = useAuth()
  const { tenants, loading, error, refresh } = useTenantDirectory()
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showCreateDemoModal, setShowCreateDemoModal] = useState(false)
  const [demoSuccess, setDemoSuccess] = useState(null)

  const filterKey = FILTERS.some((f) => f.key === searchParams.get('filter')) ? searchParams.get('filter') : 'all'
  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'all') next.delete('filter')
    else next.set('filter', key)
    setSearchParams(next, { replace: true })
  }

  useEffect(() => setPage(1), [filterKey, query])

  const views = useMemo(() => tenants.map((t) => tenantView(toTenantModel(t))), [tenants])
  const filter = FILTERS.find((f) => f.key === filterKey)
  const has = makeMatcher(query)
  const rows = views
    .filter(filter.test)
    .filter((v) => has(v.model.name, v.model.slug, v.model.billingEmail, v.model.ownerEmail))
    .sort((a, b) => RANK[b.worst] - RANK[a.worst] || a.model.name.localeCompare(b.model.name))
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Estúdios"
        subtitle={loading ? 'Carregando…' : `${rows.length} de ${tenants.length} estúdios · ordenados por gravidade`}
        actions={
          isOwner && (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateDemoModal(true)}
                title="Cria um estúdio pré-carregado com clientes, conversas e análises fictícios"
              >
                <Sparkles className="h-4 w-4" />
                Criar estúdio de demonstração
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                Novo estúdio
              </button>
            </>
          )
        }
      />

      {demoSuccess && <DemoTenantBanner demoSuccess={demoSuccess} onDismiss={() => setDemoSuccess(null)} />}

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Filtrar por nome, slug ou e-mail"
          aria-label="Filtrar estúdios"
          className="min-w-[240px] max-w-[380px] flex-1"
        />
        {FILTERS.map((f) => (
          <FilterChip
            key={f.key}
            selected={filterKey === f.key}
            onClick={() => setFilter(f.key)}
            count={loading ? undefined : views.filter(f.test).length}
          >
            {f.label}
          </FilterChip>
        ))}
      </div>

      {error && (
        <ErrorBanner>
          Falha ao carregar os estúdios: {error.response?.data?.error?.message || error.message}{' '}
          <button type="button" className="underline" onClick={refresh}>
            Tentar novamente
          </button>
        </ErrorBanner>
      )}

      <div className="card overflow-x-auto px-3 py-2">
        {loading ? (
          <Spinner />
        ) : (
          <table className="data-table min-w-[980px]">
            <thead>
              <tr>
                <th>Estúdio</th>
                <th>Saúde</th>
                <th>Plano · Cobrança</th>
                <th>WhatsApp</th>
                <th>Acesso do proprietário</th>
                <th>Clientes / Equipe</th>
                <th>Armazenamento</th>
                <th>Última atividade</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((v) => {
                const m = v.model
                return (
                  <tr
                    key={m.id}
                    onClick={() => navigate(`/tenants/${m.id}`)}
                    className="cursor-pointer hover:bg-admin-50"
                  >
                    <td className="!px-2 !py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar>{initials(m.name)}</Avatar>
                        <div className="flex min-w-0 flex-col leading-snug">
                          {/* A real link so the row is reachable by keyboard and opens in a new tab. */}
                          <a
                            href={`/tenants/${m.id}`}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              navigate(`/tenants/${m.id}`)
                            }}
                            className="font-semibold text-admin-900 hover:underline"
                          >
                            {m.name}
                          </a>
                          <span className="mono text-xs text-admin-500">{m.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Dot tone={v.worst} size={10} />
                        <span className="text-[13px]">{v.healthLabel}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col items-start gap-[3px]">
                        <span className="text-[13px]">{m.plan}</span>
                        <Tag tone={v.billing.tone}>{v.billing.label}</Tag>
                      </div>
                    </td>
                    <td>
                      <StatusLabel tone={v.wa.tone}>{v.wa.label}</StatusLabel>
                    </td>
                    <td>
                      <StatusLabel tone={v.owner.tone}>{v.owner.label}</StatusLabel>
                    </td>
                    <td className="text-[13px] tabular-nums">
                      {m.clients} / {m.users}
                    </td>
                    <td className="min-w-[120px]">
                      <div className="flex flex-col gap-1 text-xs">
                        <span>{v.storage.text}</span>
                        <ProgressBar tone={v.storage.tone} width={v.storage.width} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-[13px] text-admin-600">{formatRelative(m.lastActive)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {!loading && rows.length === 0 && (
          <p className="px-2 py-6 text-admin-600">
            Nenhum estúdio encontrado. Limpe o filtro ou pesquise em tudo com ⌘K.
          </p>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-admin-600">
            Página {page} de {pages}
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Anterior
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={page === pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      <CreateTenantModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={refresh} />
      <CreateDemoTenantModal
        isOpen={showCreateDemoModal}
        onClose={() => setShowCreateDemoModal(false)}
        onSuccess={(summary) => {
          setDemoSuccess(summary)
          refresh()
        }}
      />
    </div>
  )
}
